import React, {useState} from 'react';
import {t} from 'i18next';
import PropTypes from 'prop-types';
import {ViewMode} from '../../model/enum/ViewMode';
import InputTextComponent from '../../components/inputs/InputTextComponent';
import {DataTable} from 'primereact/datatable';
import {Column} from 'primereact/column';
import InputDropdownComponent from '../../components/inputs/InputDropdownComponent';
import ActionLink from '../../components/ActionLink';
import InputNumberComponent from '../../components/inputs/InputNumberComponent';
import InputCheckboxComponent from '../../components/inputs/InputCheckboxComponent';
import StringUtils from '../../utils/StringUtils';
import InputCalendarComponent from '../../components/inputs/InputCalendarComponent';
import Constants from '../../utils/constants';
import DateUtils from '../../utils/DateUtils';
import FileUploadComponent from '../../components/FileUploadComponent';
import {Dialog} from 'primereact/dialog';
import AuthService from '../../services/AuthService';
import DelegationService from '../../services/DelegationService';
import NumberUtils from '../../utils/NumberUtils';

export const DelegationTravelCostStep = ({
    element,
    validator,
    errorMessage,
    viewMode,
    handleChange,
    countries,
    currencies,
    paymentDictionaries,
    expenseCategoryDictionaries,
    updateElement,
}) => {
    const [showDialog, setShowDialog] = useState(false);
    const [attachmentOptions, setAttachmentOptions] = useState({
        handleChange: undefined,
        enabled: false,
        attachment: undefined,
    });
    const authService = new AuthService();
    const delegationService = new DelegationService();

    validator.addCustomRule(
        'date_start_not_bigger_than_date_to',
        'Czas wyjazdu nie może być późniejszy niż Czas przyjazdu',
        (value, params) => {
            const index = parseInt(params[0]);
            const vehicleLog = element.vehicleLogs[index];
            if (vehicleLog?.arrivalDate && vehicleLog?.departureDate) {
                const startDate =
                    typeof vehicleLog?.arrivalDate === 'string'
                        ? DateUtils.dateFromString(vehicleLog?.arrivalDate)
                        : vehicleLog?.arrivalDate;
                const endDate =
                    typeof vehicleLog?.departureDate === 'string'
                        ? DateUtils.dateFromString(vehicleLog?.departureDate)
                        : vehicleLog?.departureDate;
                return endDate.getTime() <= startDate.getTime();
            }
            return true;
        },
        (message, params) => message
    );

    const actionTemplate = (
        props,
        rowData,
        handleRemoveClick,
        attachmentOpt = {handleChange: undefined, enabled: false, attachment: undefined}
    ) => {
        return (
            <span>
                {viewMode !== ViewMode.VIEW && (
                    <ActionLink
                        iconName='mdi-delete'
                        iconSide='left'
                        iconSize='icon-large'
                        handleClick={handleRemoveClick}
                        variant='blue'
                        className='edit-blue-link'
                        key={'delete-button'}
                    />
                )}
                {attachmentOpt.enabled && (
                    <div className='mt-3'>
                        <ActionLink
                            iconName='mdi-attachment'
                            iconSide='left'
                            iconSize='icon-large'
                            handleClick={() => {
                                setShowDialog(true);
                                setAttachmentOptions(attachmentOpt);
                            }}
                            variant='green'
                            className={rowData.receipt ? 'edit-green-link' : 'edit-blue-link'}
                            key={'delete-button'}
                        />
                    </div>
                )}
            </span>
        );
    };

    const countryTemplate = (rowData, props, stateField, countries, type) => {
        const {rowIndex} = props;
        const element = rowData;
        return (
            <InputDropdownComponent
                id={`country-${rowIndex}-${type}`}
                name='country'
                value={element.country}
                stateField={stateField}
                showLabel={false}
                label='Kraj'
                options={countries}
                optionLabel='name'
                onChange={handleChange}
                insideTable
                viewMode={viewMode}
                validator={validator}
                validators='required'
                colClass='col-12'
                filter
                filterBy='name'
            />
        );
    };
    const moneyCurrencyTemplate = (element, props, stateField, entityName) => {
        const selectedCurrency = element?.amount?.currency ? element.amount.currency : 'PLN';
        const isOnlyPolandCurrency = isDomestic();
        return (
            <div className='row'>
                <InputNumberComponent
                    id={`amount-${props.rowIndex}-${entityName}`}
                    name='value'
                    label={'Kwota'}
                    value={element?.amount?.value}
                    onChange={handleChange}
                    viewMode={viewMode}
                    showLabel={false}
                    validator={validator}
                    disabled={StringUtils.isBlank(element.amount)}
                    validators={StringUtils.isBlank(element.amount) ? 'not_required' : 'required'}
                    mode={isOnlyPolandCurrency ? 'currency' : 'decimal'}
                    colClass={isOnlyPolandCurrency ? 'col-12' : 'col-6'}
                    currency={selectedCurrency}
                    locale='pl-PL'
                    stateField={stateField}
                />
                {!isOnlyPolandCurrency && (
                    <InputDropdownComponent
                        id={`currency-${props.rowIndex}-${entityName}`}
                        name='currency'
                        value={selectedCurrency}
                        showLabel={false}
                        label='Rodzaj pojazdu'
                        options={currencies}
                        onChange={handleChange}
                        disabled={StringUtils.isBlank(element.amount)}
                        viewMode={viewMode}
                        validator={validator}
                        colClass='col-6'
                        validators={StringUtils.isBlank(element.amount) ? 'not_required' : 'required'}
                        stateField={stateField}
                    />
                )}
            </div>
        );
    };
    const afterChangeNoReceipt = (rowData, c) => {
        if (c.checked) {
            rowData.amount = undefined;
            rowData.paymentDate = undefined;
            rowData.typeOfPayment = undefined;
            rowData.receipt = undefined;
        } else {
            rowData.amount = {value: undefined, currency: undefined};
        }
        return rowData;
    };
    const handleReceiptChange = (arrayName, rowData, newReceipt) => {
        element[arrayName].forEach((lt) => {
            if (lt === rowData) {
                lt.receipt = newReceipt;
            }
        });
        updateElement(element);
    };

    const renderAttachementDialog = () => {
        return (
            <Dialog
                header='Załącznik'
                visible={true}
                style={{width: '45vw'}}
                onHide={() => {
                    setAttachmentOptions({handleChange: undefined, enabled: false, attachment: undefined});
                    setShowDialog(false);
                }}
            >
                <FileUploadComponent
                    key='receipt-localTransport'
                    alt='Paragon'
                    handleRemove={() => {
                        attachmentOptions.handleChange();
                    }}
                    errorMessage={errorMessage}
                    srcUrl={`${delegationService.getPath()}/receipt/${attachmentOptions.attachment?.uuid}?token=${authService?.getToken()}`}
                    maxFileSize={150000000}
                    fileSizeToTranslation={''}
                    initAttachment={attachmentOptions.attachment}
                    handleAdd={(e) => {
                        attachmentOptions.handleChange(e);
                    }}
                    required={true}
                    validator={validator}
                    viewMode={viewMode}
                />
            </Dialog>
        );
    };
    const renderAccommodations = () => {
        return (
            <div>
                <label className='p-label mt-4'>
                    <h6>Noclegi</h6>{' '}
                </label>
                <DataTable value={element?.accommodations} responsiveLayout='scroll' emptyMessage={t('list.empty')} className=''>
                    <Column
                        header='Brak rachunku'
                        body={(rowData) => {
                            return (
                                <InputCheckboxComponent
                                    insideTable={true}
                                    labelOnRight={true}
                                    id={`noReceipt`}
                                    name='noReceipt'
                                    showLabel={false}
                                    label={'Brak rachunku - Zastosuj ryczałt'}
                                    value={StringUtils.isBlank(rowData.amount)}
                                    validator={validator}
                                    validators='not_required'
                                    stateField=''
                                    onChange={(a, b, c) => {
                                        const accommodations = element.accommodations.filter((pa) => pa !== rowData);
                                        const updatedAccommodation = afterChangeNoReceipt(rowData, c);
                                        accommodations.push(updatedAccommodation);
                                        element.accommodations = accommodations;
                                        updateElement(element);
                                    }}
                                    disabled={viewMode === 'VIEW'}
                                    viewMode={viewMode === 'VIEW' ? 'EDIT' : viewMode}
                                />
                            );
                        }}
                    />
                    {isForeign() && (
                        <Column
                            field='country'
                            header='Kraj'
                            style={{minWidth: '200px'}}
                            body={(data, props) => {
                                return countryTemplate(
                                    data,
                                    props,
                                    `element.accommodations[${props.rowIndex}]`,
                                    countries,
                                    'accommodation'
                                );
                            }}
                        />
                    )}
                    <Column
                        field='days'
                        header='Liczba noclegów'
                        style={{minWidth: '100px'}}
                        body={(element, props) => {
                            return (
                                <InputNumberComponent
                                    id={`days-${props.rowIndex}-accommodation`}
                                    name='days'
                                    label={'Liczba noclegów'}
                                    showLabel={false}
                                    value={element?.days}
                                    onChange={handleChange}
                                    viewMode={viewMode}
                                    colClass='col-12 '
                                    validator={validator}
                                    validators='required'
                                    locale='pl-PL'
                                    stateField={`element.accommodations[${props.rowIndex}]`}
                                />
                            );
                        }}
                    />
                    <Column
                        field='amount'
                        header='Kwota'
                        body={(element, props) => {
                            return moneyCurrencyTemplate(
                                element,
                                props,
                                `element.accommodations[${props.rowIndex}].amount`,
                                'accommodation'
                            );
                        }}
                    />
                    <Column
                        field='paymentDate'
                        header='Data płatności'
                        colClass='col-12 '
                        body={(element, props) => {
                            return (
                                <InputCalendarComponent
                                    id={`paymentDate-${props.rowIndex}-accommodation`}
                                    colClass='col-12'
                                    name='paymentDate'
                                    label='Data płatności'
                                    dateFormatMoment={Constants.DATE_FORMAT}
                                    placeholder='DD-MM-YYYY'
                                    value={StringUtils.isString(element.paymentDate) ? new Date(element.paymentDate) : element.paymentDate}
                                    onChange={handleChange}
                                    viewMode={viewMode}
                                    validator={validator}
                                    disabled={StringUtils.isBlank(element.amount)}
                                    showTime={false}
                                    showSeconds={false}
                                    validators={StringUtils.isBlank(element.amount) ? 'not_required' : 'required'}
                                    stateField={`element.accommodations[${props.rowIndex}]`}
                                    insideTable
                                    showLabel={false}
                                    showIcon
                                />
                            );
                        }}
                    />
                    <Column
                        field='typeOfPayment'
                        header='Rodzaj płatności'
                        style={{minWidth: '200px'}}
                        colClass='col-12 '
                        body={(element, props) => {
                            return (
                                <InputDropdownComponent
                                    id={`typeOfPayment${props.rowIndex}-accommodation`}
                                    name='typeOfPayment'
                                    value={element.typeOfPayment}
                                    stateField={`element.accommodations[${props.rowIndex}]`}
                                    showLabel={false}
                                    label='Rodzaj płatności'
                                    options={paymentDictionaries}
                                    optionLabel='name'
                                    onChange={handleChange}
                                    insideTable
                                    disabled={StringUtils.isBlank(element.amount)}
                                    dataKey='id'
                                    viewMode={viewMode}
                                    validator={validator}
                                    validators={StringUtils.isBlank(element.amount) ? 'not_required' : 'required'}
                                    colClass='col-12'
                                    valueView={element.typeOfPayment?.name}
                                />
                            );
                        }}
                    />
                    <Column
                        field='comment'
                        header='Komentarz'
                        body={(element, props) => {
                            return (
                                <InputTextComponent
                                    id={`comment-${props.rowIndex}-accommodation`}
                                    name='comment'
                                    label='Komentarz'
                                    colClass='col-12 '
                                    value={element?.comment}
                                    showLabel={false}
                                    validator={validator}
                                    validators='not_required|max:1024'
                                    onChange={handleChange}
                                    viewMode={viewMode}
                                    stateField={`element.accommodations[${props.rowIndex}]`}
                                />
                            );
                        }}
                    />
                    <Column
                        header=''
                        body={(rowData, props) => {
                            const attachmentOpt = {
                                enabled: !StringUtils.isBlank(rowData.amount),
                                handleChange: (e) => handleReceiptChange('accommodations', rowData, e),
                                attachment: rowData.receipt,
                            };
                            return actionTemplate(
                                props,
                                rowData,
                                () => {
                                    const accommodations = element.accommodations.filter((pa) => pa !== rowData);
                                    element.accommodations = accommodations;
                                    updateElement(element);
                                },
                                attachmentOpt
                            );
                        }}
                    />
                </DataTable>
                {viewMode !== ViewMode.VIEW && (
                    <ActionLink
                        label={'Dodaj nocleg'}
                        id='add-accommodation'
                        iconName='mdi-plus'
                        iconSide='left'
                        iconSize='icon-large'
                        variant='blue'
                        className='float-right'
                        key={'add-activity-button'}
                        handleClick={() => {
                            const newElement = {
                                country: undefined,
                                days: undefined,
                                typeOfPayment: undefined,
                                paymentDate: undefined,
                                amount: undefined,
                                comment: undefined,
                            };
                            element.accommodations.push(newElement);
                            updateElement(element);
                        }}
                    />
                )}
            </div>
        );
    };

    const renderExpenses = () => {
        return (
            <div>
                <label className='p-label mt-4'>
                    <h6>Wydatki</h6>{' '}
                </label>
                <DataTable value={element?.expenses} responsiveLayout='' emptyMessage={t('list.empty')} className=''>
                    <Column
                        header='Brak rachunku'
                        body={(rowData) => {
                            return (
                                <InputCheckboxComponent
                                    insideTable={true}
                                    labelOnRight={true}
                                    id={`noReceipt`}
                                    name='noReceipt'
                                    showLabel={false}
                                    label={'Brak rachunku - Zastosuj ryczałt'}
                                    value={StringUtils.isBlank(rowData.amount)}
                                    validator={validator}
                                    validators='not_required'
                                    stateField=''
                                    onChange={(a, b, c) => {
                                        const expenses = element.expenses.filter((pa) => pa !== rowData);
                                        const updatedExpense = afterChangeNoReceipt(rowData, c);
                                        expenses.push(updatedExpense);
                                        element.expenses = expenses;
                                        updateElement(element);
                                    }}
                                    disabled={viewMode === 'VIEW'}
                                    viewMode={viewMode === 'VIEW' ? 'EDIT' : viewMode}
                                />
                            );
                        }}
                    />
                    {isForeign() && (
                        <Column
                            field='country'
                            header='Kraj'
                            style={{minWidth: '200px'}}
                            body={(data, props) => {
                                return countryTemplate(data, props, `element.expenses[${props.rowIndex}]`, countries, 'expense');
                            }}
                        />
                    )}
                    <Column
                        field='category'
                        header='Kategoria wydatku'
                        style={{minWidth: '200px'}}
                        colClass='col-12 '
                        body={(element, props) => {
                            return (
                                <InputDropdownComponent
                                    id={`category${props.rowIndex}-expense`}
                                    name='category'
                                    value={element.category}
                                    stateField={`element.expenses[${props.rowIndex}]`}
                                    showLabel={false}
                                    label='Kategoria wydatku'
                                    options={expenseCategoryDictionaries}
                                    optionLabel='name'
                                    onChange={handleChange}
                                    insideTable
                                    disabled={StringUtils.isBlank(element.amount)}
                                    dataKey='id'
                                    viewMode={viewMode}
                                    validator={validator}
                                    validators={StringUtils.isBlank(element.amount) ? 'not_required' : 'required'}
                                    colClass='col-12'
                                    valueView={element.category?.name}
                                />
                            );
                        }}
                    />
                    <Column
                        field='amount'
                        header='Kwota'
                        body={(element, props) => {
                            return moneyCurrencyTemplate(element, props, `element.expenses[${props.rowIndex}].amount`, 'expense');
                        }}
                    />
                    <Column
                        field='paymentDate'
                        header='Data płatności'
                        colClass='col-12 '
                        body={(element, props) => {
                            return (
                                <InputCalendarComponent
                                    id={`paymentDate-${props.rowIndex}-expense`}
                                    colClass='col-12'
                                    name='paymentDate'
                                    label='Data płatności'
                                    dateFormatMoment={Constants.DATE_FORMAT}
                                    placeholder='DD-MM-YYYY'
                                    value={StringUtils.isString(element.paymentDate) ? new Date(element.paymentDate) : element.paymentDate}
                                    onChange={handleChange}
                                    viewMode={viewMode}
                                    validator={validator}
                                    showTime={false}
                                    disabled={StringUtils.isBlank(element.amount)}
                                    showSeconds={false}
                                    validators={StringUtils.isBlank(element.amount) ? 'not_required' : 'required'}
                                    stateField={`element.expenses[${props.rowIndex}]`}
                                    insideTable
                                    showLabel={false}
                                    showIcon
                                />
                            );
                        }}
                    />
                    <Column
                        field='typeOfPayment'
                        header='Rodzaj płatności'
                        style={{minWidth: '200px'}}
                        colClass='col-12 '
                        body={(element, props) => {
                            return (
                                <InputDropdownComponent
                                    id={`typeOfPayment${props.rowIndex}-expense`}
                                    name='typeOfPayment'
                                    value={element.typeOfPayment}
                                    stateField={`element.expenses[${props.rowIndex}]`}
                                    showLabel={false}
                                    label='Rodzaj płatności'
                                    options={paymentDictionaries}
                                    optionLabel='name'
                                    onChange={handleChange}
                                    insideTable
                                    disabled={StringUtils.isBlank(element.amount)}
                                    dataKey='id'
                                    viewMode={viewMode}
                                    validator={validator}
                                    validators={StringUtils.isBlank(element.amount) ? 'not_required' : 'required'}
                                    colClass='col-12'
                                    valueView={element.typeOfPayment?.name}
                                />
                            );
                        }}
                    />
                    <Column
                        field='comment'
                        header='Komentarz'
                        body={(element, props) => {
                            return (
                                <InputTextComponent
                                    id={`comment-${props.rowIndex}-expense`}
                                    name='comment'
                                    label='Komentarz'
                                    colClass='col-12 '
                                    value={element?.comment}
                                    showLabel={false}
                                    validator={validator}
                                    validators='not_required|max:1024'
                                    onChange={handleChange}
                                    viewMode={viewMode}
                                    stateField={`element.expenses[${props.rowIndex}]`}
                                />
                            );
                        }}
                    />
                    <Column
                        header=''
                        body={(rowData, props) => {
                            const attachmentOpt = {
                                enabled: !StringUtils.isBlank(rowData.amount),
                                handleChange: (e) => handleReceiptChange('expenses', rowData, e),
                                attachment: rowData.receipt,
                            };
                            return actionTemplate(
                                props,
                                rowData,
                                () => {
                                    const expenses = element.expenses.filter((pa) => pa !== rowData);
                                    element.expenses = expenses;
                                    updateElement(element);
                                },
                                attachmentOpt
                            );
                        }}
                    />
                </DataTable>
                {viewMode !== ViewMode.VIEW && (
                    <ActionLink
                        label={'Dodaj wydatek'}
                        id='add-exepnse'
                        iconName='mdi-plus'
                        iconSide='left'
                        iconSize='icon-large'
                        variant='blue'
                        className='float-right'
                        key={'add-expense-button'}
                        handleClick={() => {
                            const newElement = {
                                country: undefined,
                                category: undefined,
                                amount: undefined,
                                comment: undefined,
                                typeOfPayment: undefined,
                                paymentDate: undefined,
                            };
                            element.expenses.push(newElement);
                            updateElement(element);
                        }}
                    />
                )}
            </div>
        );
    };
    const renderVehicleLogs = () => {
        return (
            <div>
                <label className='p-label mt-4'>
                    <h6>Ewidencja przejazdów pojazdem</h6>{' '}
                </label>
                <DataTable value={element?.vehicleLogs} responsiveLayout='scroll' emptyMessage={t('list.empty')} className=''>
                    <Column
                        field='status'
                        header='Rodzaj pojazdu'
                        colClass='col-12 '
                        body={(rowData, props) => {
                            console.log(rowData.status, 'rowData.status');
                            return (
                                <InputDropdownComponent
                                    id={`status-${props.rowIndex}-vehicleLog`}
                                    name='status'
                                    value={rowData.status}
                                    stateField={`element.vehicleLogs[${props.rowIndex}]`}
                                    showLabel={false}
                                    label='Rodzaj pojazdu'
                                    options={[
                                        {enumValue: 'COMPANY', label: 'Firmowy'},
                                        {enumValue: 'PRIVATE', label: 'Prywatny'},
                                    ]}
                                    onChange={(a, b, c, d, e) => {
                                        element?.vehicleLogs?.forEach((vehicleLog) => {
                                            if (vehicleLog === rowData) {
                                                if (c.value.enumValue === 'COMPANY') {
                                                    vehicleLog.type = undefined;
                                                    vehicleLog.distanceInKilometers = undefined;
                                                } else {
                                                    vehicleLog.departureOdometerInKilometers = undefined;
                                                    vehicleLog.arrivalOdometerInKilometers = undefined;
                                                }
                                            }
                                        });
                                        handleChange(a, b, c, d, e);
                                        updateElement(element);
                                    }}
                                    insideTable
                                    viewMode={viewMode}
                                    validator={validator}
                                    validators={'required'}
                                    colClass='col-12'
                                    optionLabel={'label'}
                                    dataKey='enumValue'
                                />
                            );
                        }}
                    />
                    <Column
                        field='type'
                        header='Typ pojazdu'
                        colClass='col-12 '
                        body={(element, props) => {
                            const isRequired = element?.status?.enumValue === 'PRIVATE';
                            return (
                                <InputDropdownComponent
                                    id={`type-${props.rowIndex}-vehicleLog`}
                                    name='type'
                                    value={element.type}
                                    stateField={`element.vehicleLogs[${props.rowIndex}]`}
                                    showLabel={false}
                                    label='Typ pojazdu'
                                    options={[
                                        {enumValue: 'UNDER900', label: 'Samochód (powyżej 900m3)'},
                                        {enumValue: 'ABOVE900', label: 'Samochód (poniżej 900m3)'},
                                        {enumValue: 'MOTORBIKE', label: 'Motorower'},
                                        {enumValue: 'MOPED', label: 'Motor'},
                                    ]}
                                    onChange={handleChange}
                                    insideTable
                                    viewMode={viewMode}
                                    validator={validator}
                                    disabled={!isRequired}
                                    validators={isRequired ? 'required' : 'not_required'}
                                    colClass='col-12'
                                    optionLabel={'label'}
                                    dataKey='enumValue'
                                />
                            );
                        }}
                    />
                    <Column
                        field='departure'
                        header='Wyjazd z'
                        style={{minWidth: '130px'}}
                        body={(element, props) => {
                            return (
                                <InputTextComponent
                                    id={`departure-${props.rowIndex}-vehicleLog`}
                                    name='departure'
                                    label='Wyjazd z'
                                    colClass='col-12 '
                                    value={element?.departure}
                                    showLabel={false}
                                    validator={validator}
                                    validators='required|max:255'
                                    onChange={handleChange}
                                    viewMode={viewMode}
                                    stateField={`element.vehicleLogs[${props.rowIndex}]`}
                                />
                            );
                        }}
                    />
                    <Column
                        field='departureDate'
                        header='Czas wyjazdu'
                        colClass='col-12 '
                        style={{minWidth: '230px'}}
                        body={(element, props) => {
                            const {rowIndex} = props;
                            return (
                                <InputCalendarComponent
                                    id={`departureDate-${props.rowIndex}-vehicleLog`}
                                    colClass='col-12'
                                    name='departureDate'
                                    label='Czas wyjazdu'
                                    dateFormatMoment={Constants.DATE_TIME_FORMAT}
                                    placeholder='DD-MM-YYYY HH:mm'
                                    value={
                                        StringUtils.isString(element.departureDate)
                                            ? new Date(element.departureDate)
                                            : element.departureDate
                                    }
                                    onChange={handleChange}
                                    viewMode={viewMode}
                                    validator={validator}
                                    validators={`required|date_start_not_bigger_than_date_to:${rowIndex}`}
                                    stateField={`element.vehicleLogs[${props.rowIndex}]`}
                                    insideTable
                                    showLabel={false}
                                    showIcon
                                    showTime
                                    showSeconds={false}
                                />
                            );
                        }}
                    />
                    <Column
                        field='departureOdometerInKilometers'
                        header='Stan licznika w km (wyjazd)'
                        style={{minWidth: '100px'}}
                        body={(element, props) => {
                            const isRequired = element?.status?.enumValue === 'COMPANY';
                            return (
                                <InputNumberComponent
                                    id={`departureOdometerInKilometers-${props.rowIndex}-vehicleLog`}
                                    name='departureOdometerInKilometers'
                                    label={'Stan licznika w km (wyjazd)'}
                                    showLabel={false}
                                    value={element?.departureOdometerInKilometers}
                                    onChange={handleChange}
                                    viewMode={viewMode}
                                    colClass='col-12 '
                                    mode='decimal'
                                    maxFractionDigits={2}
                                    disabled={!isRequired}
                                    validator={validator}
                                    validators={isRequired ? 'required' : 'not_required'}
                                    stateField={`element.vehicleLogs[${props.rowIndex}]`}
                                />
                            );
                        }}
                    />
                    <Column
                        field='arrival'
                        header='Przyjazd do'
                        style={{minWidth: '130px'}}
                        body={(element, props) => {
                            return (
                                <InputTextComponent
                                    id={`arrival-${props.rowIndex}-vehicleLog`}
                                    name='arrival'
                                    label='Przyjazd do'
                                    colClass='col-12 '
                                    value={element?.arrival}
                                    showLabel={false}
                                    validator={validator}
                                    validators='required|max:255'
                                    onChange={handleChange}
                                    viewMode={viewMode}
                                    stateField={`element.vehicleLogs[${props.rowIndex}]`}
                                />
                            );
                        }}
                    />
                    <Column
                        field='arrivalDate'
                        header='Czas przyjazdu'
                        style={{minWidth: '230px'}}
                        colClass='col-12 '
                        body={(element, props) => {
                            const {rowIndex} = props;
                            return (
                                <InputCalendarComponent
                                    id={`arrivalDate-${props.rowIndex}-vehicleLog`}
                                    colClass='col-12'
                                    name='arrivalDate'
                                    label='Czas wyjazdu'
                                    dateFormatMoment={Constants.DATE_TIME_FORMAT_WITHOUT_SEC}
                                    placeholder='DD-MM-YYYY HH:mm'
                                    value={StringUtils.isString(element.arrivalDate) ? new Date(element.arrivalDate) : element.arrivalDate}
                                    onChange={handleChange}
                                    viewMode={viewMode}
                                    validator={validator}
                                    validators={`required|date_start_not_bigger_than_date_to:${rowIndex}`}
                                    stateField={`element.vehicleLogs[${props.rowIndex}]`}
                                    insideTable
                                    showLabel={false}
                                    showIcon
                                    showTime
                                    showSeconds={false}
                                />
                            );
                        }}
                    />
                    <Column
                        field='arrivalOdometerInKilometers'
                        header='Stan licznika w km (wjazd)'
                        style={{minWidth: '100px'}}
                        body={(element, props) => {
                            const isRequired = element?.status?.enumValue === 'COMPANY';
                            return (
                                <InputNumberComponent
                                    id={`arrivalOdometerInKilometers-${props.rowIndex}-vehicleLog`}
                                    name='arrivalOdometerInKilometers'
                                    label={'Stan licznika w km (wjazd)'}
                                    showLabel={false}
                                    value={element?.arrivalOdometerInKilometers}
                                    onChange={handleChange}
                                    viewMode={viewMode}
                                    colClass='col-12 '
                                    mode='decimal'
                                    maxFractionDigits={2}
                                    disabled={!isRequired}
                                    validator={validator}
                                    validators={isRequired ? 'required' : 'not_required'}
                                    stateField={`element.vehicleLogs[${props.rowIndex}]`}
                                />
                            );
                        }}
                    />
                    <Column
                        field='distanceInKilometers'
                        header='Przebyta odległość w km'
                        style={{minWidth: '100px'}}
                        body={(element, props) => {
                            const isRequired = element?.status?.enumValue === 'PRIVATE';
                            return (
                                <InputNumberComponent
                                    id={`distanceInKilometers-${props.rowIndex}-vehicleLog`}
                                    name='distanceInKilometers'
                                    label={'Przebyta odległość w km'}
                                    showLabel={false}
                                    value={element?.distanceInKilometers}
                                    onChange={handleChange}
                                    viewMode={viewMode}
                                    mode='decimal'
                                    colClass='col-12 '
                                    maxFractionDigits={2}
                                    disabled={!isRequired}
                                    validator={validator}
                                    validators={isRequired ? 'required' : 'not_required'}
                                    stateField={`element.vehicleLogs[${props.rowIndex}]`}
                                />
                            );
                        }}
                    />
                    <Column
                        header=''
                        body={(rowData, props) => {
                            return actionTemplate(props, rowData, () => {
                                const vehicleLogs = element.vehicleLogs.filter((pa) => pa !== rowData);
                                element.vehicleLogs = vehicleLogs;
                                updateElement(element);
                            });
                        }}
                    />
                </DataTable>
                {viewMode !== ViewMode.VIEW && (
                    <ActionLink
                        label={'Dodaj przejazd'}
                        id='add-vehicleLog'
                        iconName='mdi-plus'
                        iconSide='left'
                        iconSize='icon-large'
                        variant='blue'
                        className='float-right'
                        key={'add-vehicleLog-button'}
                        handleClick={() => {
                            const newElement = {
                                status: undefined,
                                type: undefined,
                                arrival: undefined,
                                arrivalDate: undefined,
                                arrivalOdometerInKilometers: undefined,
                                departure: undefined,
                                departureDate: undefined,
                                departureOdometerInKilometers: undefined,
                                distanceInKilometers: undefined,
                            };
                            element.vehicleLogs.push(newElement);
                            updateElement(element);
                        }}
                    />
                )}
            </div>
        );
    };

    const renderHospitalStays = () => {
        return (
            <div>
                <label className='p-label mt-4'>
                    <h6>Pobyt w szpitalu</h6>{' '}
                </label>
                <DataTable value={element?.hospitalStays} responsiveLayout='scroll' emptyMessage={t('list.empty')} className=''>
                    <Column
                        field='country'
                        header='Kraj'
                        style={{minWidth: '200px'}}
                        body={(data, props) => {
                            return countryTemplate(data, props, `element.hospitalStays[${props.rowIndex}]`, countries, 'hospitalStay');
                        }}
                    />
                    <Column
                        field='days'
                        header='Liczba dni pobytu'
                        style={{minWidth: '200px'}}
                        body={(element, props) => {
                            return (
                                <InputNumberComponent
                                    id={`days-${props.rowIndex}-hospitalStay`}
                                    name='days'
                                    label={'Liczba dni pobytu'}
                                    showLabel={false}
                                    value={element?.days}
                                    onChange={handleChange}
                                    viewMode={viewMode}
                                    colClass='col-12 '
                                    validator={validator}
                                    validators='required'
                                    locale='pl-PL'
                                    stateField={`element.hospitalStays[${props.rowIndex}]`}
                                />
                            );
                        }}
                    />
                    <Column
                        field='place'
                        header='Miejscowość'
                        body={(element, props) => {
                            return (
                                <InputTextComponent
                                    id={`place-${props.rowIndex}-hospitalStay`}
                                    name='place'
                                    label='Miejscowość'
                                    colClass='col-12 '
                                    value={element?.place}
                                    showLabel={false}
                                    validator={validator}
                                    validators='required|max:255'
                                    onChange={handleChange}
                                    viewMode={viewMode}
                                    stateField={`element.hospitalStays[${props.rowIndex}]`}
                                />
                            );
                        }}
                    />
                    <Column
                        header=''
                        body={(rowData, props) => {
                            return actionTemplate(props, rowData, () => {
                                const hospitalStays = element.hospitalStays.filter((pa) => pa !== rowData);
                                element.hospitalStays = hospitalStays;
                                updateElement(element);
                            });
                        }}
                    />
                </DataTable>
                {viewMode !== ViewMode.VIEW && (
                    <ActionLink
                        label={'Dodaj pobyt'}
                        id='add-hospitalStay'
                        iconName='mdi-plus'
                        iconSide='left'
                        iconSize='icon-large'
                        variant='blue'
                        className='float-right'
                        key={'add-hospitalStay-button'}
                        handleClick={() => {
                            const newElement = {
                                country: undefined,
                                days: undefined,
                                place: undefined,
                            };
                            element.hospitalStays.push(newElement);
                            updateElement(element);
                        }}
                    />
                )}
            </div>
        );
    };
    const renderlocalTransports = () => {
        return (
            <div>
                <label className='p-label mt-4'>
                    <h6>Komunikacja miejska</h6>{' '}
                </label>
                <DataTable value={element?.localTransports} responsiveLayout='scroll' emptyMessage={t('list.empty')} className=''>
                    {isDomestic() && (
                        <Column
                            header='Brak rachunku'
                            body={(rowData) => {
                                return (
                                    <InputCheckboxComponent
                                        insideTable={true}
                                        labelOnRight={true}
                                        id={`noReceipt`}
                                        name='noReceipt'
                                        showLabel={false}
                                        label={'Brak rachunku - Zastosuj ryczałt'}
                                        value={StringUtils.isBlank(rowData.amount)}
                                        validator={validator}
                                        validators='not_required'
                                        stateField=''
                                        onChange={(a, b, c) => {
                                            const localTransports = element.localTransports.filter((pa) => pa !== rowData);
                                            const localTransport = afterChangeNoReceipt(rowData, c);
                                            localTransports.push(localTransport);
                                            element.localTransports = localTransports;
                                            updateElement(element);
                                        }}
                                        disabled={viewMode === 'VIEW'}
                                        viewMode={viewMode === 'VIEW' ? 'EDIT' : viewMode}
                                    />
                                );
                            }}
                        />
                    )}
                    {isForeign() && (
                        <Column
                            field='country'
                            header='Kraj'
                            style={{minWidth: '200px'}}
                            body={(data, props) => {
                                return countryTemplate(
                                    data,
                                    props,
                                    `element.localTransports[${props.rowIndex}]`,
                                    countries,
                                    'localTransport'
                                );
                            }}
                        />
                    )}

                    <Column
                        field='days'
                        header='Liczba dni dojazdów'
                        style={{minWidth: '200px'}}
                        body={(element, props) => {
                            return (
                                <InputNumberComponent
                                    id={`days-${props.rowIndex}-localTransport`}
                                    name='days'
                                    label={'Liczba dni dojazdów'}
                                    showLabel={false}
                                    value={element?.days}
                                    onChange={handleChange}
                                    viewMode={viewMode}
                                    colClass='col-12 '
                                    validator={validator}
                                    validators='required'
                                    locale='pl-PL'
                                    stateField={`element.localTransports[${props.rowIndex}]`}
                                />
                            );
                        }}
                    />
                    {isDomestic() && (
                        <Column
                            field='amount'
                            header='Kwota'
                            style={{minWidth: '200px'}}
                            body={(element, props) => {
                                return (
                                    <InputNumberComponent
                                        id={`amount-${props.rowIndex}-localTransport`}
                                        name='value'
                                        label={'Kwota'}
                                        value={element?.amount?.value}
                                        onChange={handleChange}
                                        viewMode={viewMode}
                                        showLabel={false}
                                        validator={validator}
                                        validators={StringUtils.isBlank(element.amount) ? 'not_required' : 'required'}
                                        disabled={StringUtils.isBlank(element.amount)}
                                        mode='currency'
                                        colClass='col-12 '
                                        currency='PLN'
                                        locale='pl-PL'
                                        stateField={`element.localTransports[${props.rowIndex}].amount`}
                                    />
                                );
                            }}
                        />
                    )}
                    {isDomestic() && (
                        <Column
                            field='typeOfPayment'
                            header='Rodzaj płatności'
                            style={{minWidth: '200px'}}
                            colClass='col-12 '
                            body={(element, props) => {
                                return (
                                    <InputDropdownComponent
                                        id={`typeOfPayment${props.rowIndex}-localtransport`}
                                        name='typeOfPayment'
                                        value={element.typeOfPayment}
                                        stateField={`element.localTransports[${props.rowIndex}]`}
                                        showLabel={false}
                                        label='Rodzaj płatności'
                                        options={paymentDictionaries}
                                        optionLabel='name'
                                        onChange={handleChange}
                                        insideTable
                                        disabled={StringUtils.isBlank(element.amount)}
                                        dataKey='id'
                                        viewMode={viewMode}
                                        validator={validator}
                                        validators={StringUtils.isBlank(element.amount) ? 'not_required' : 'required'}
                                        colClass='col-12'
                                        valueView={element.typeOfPayment?.name}
                                    />
                                );
                            }}
                        />
                    )}
                    {isDomestic() && (
                        <Column
                            field='comment'
                            header='Komentarz'
                            body={(element, props) => {
                                return (
                                    <InputTextComponent
                                        id={`comment-${props.rowIndex}-localTransport`}
                                        name='comment'
                                        label='Komentarz'
                                        colClass='col-12 '
                                        value={element?.comment}
                                        showLabel={false}
                                        validator={validator}
                                        validators='not_required|max:1024'
                                        onChange={handleChange}
                                        viewMode={viewMode}
                                        stateField={`element.localTransports[${props.rowIndex}]`}
                                    />
                                );
                            }}
                        />
                    )}
                    <Column
                        header=''
                        body={(rowData, props) => {
                            const attachmentOpt = {
                                enabled: (rowData.attachmentEnabled = isDomestic() && !StringUtils.isBlank(rowData.amount)),
                                handleChange: (e) => handleReceiptChange('localTransports', rowData, e),
                                attachment: rowData.receipt,
                            };
                            return actionTemplate(
                                props,
                                rowData,
                                () => {
                                    const localTransports = element.localTransports.filter((pa) => pa !== rowData);
                                    element.localTransports = localTransports;
                                    updateElement(element);
                                },
                                attachmentOpt
                            );
                        }}
                    />
                </DataTable>
                {viewMode !== ViewMode.VIEW && (
                    <ActionLink
                        label={'Dodaj przejazd'}
                        id='add-localTransport'
                        iconName='mdi-plus'
                        iconSide='left'
                        iconSize='icon-large'
                        variant='blue'
                        className='float-right'
                        key={'add-local-button'}
                        handleClick={() => {
                            const newElement = {
                                country: undefined,
                                comment: undefined,
                                typeOfPayment: undefined,
                                amount: undefined,
                                days: undefined,
                            };
                            element.localTransports.push(newElement);
                            updateElement(element);
                        }}
                    />
                )}
            </div>
        );
    };
    const renderTransfers = () => {
        return (
            <div>
                <label className='p-label mt-4'>
                    <h6>{isForeign() ? 'Dojazd do/z dworca/portu' : 'Przejazdy'}</h6>{' '}
                </label>
                <DataTable value={element?.transfers} responsiveLayout='scroll' emptyMessage={t('list.empty')} className=''>
                    {isDomestic() && (
                        <Column
                            header='Brak rachunku'
                            body={(rowData) => {
                                return (
                                    <InputCheckboxComponent
                                        insideTable={true}
                                        labelOnRight={true}
                                        id={`noReceipt`}
                                        name='noReceipt'
                                        showLabel={false}
                                        label={'Brak rachunku - Zastosuj ryczałt'}
                                        value={StringUtils.isBlank(rowData.amount)}
                                        validator={validator}
                                        validators='not_required'
                                        stateField=''
                                        onChange={(a, b, c) => {
                                            const transfers = element.transfers.filter((pa) => pa !== rowData);
                                            const transfer = afterChangeNoReceipt(rowData, c);
                                            transfers.push(transfer);
                                            element.transfers = transfers;
                                            updateElement(element);
                                        }}
                                        disabled={viewMode === 'VIEW'}
                                        viewMode={viewMode === 'VIEW' ? 'EDIT' : viewMode}
                                    />
                                );
                            }}
                        />
                    )}
                    {isForeign() && (
                        <Column
                            field='country'
                            header='Kraj docelowy'
                            style={{minWidth: '200px'}}
                            body={(data, props) => {
                                let countriesAvailable = countries;
                                if (isForeign()) {
                                    countriesAvailable = [];
                                    const destiantions = element.destinations;
                                    countries.forEach((country) => {
                                        if (country?.name?.toUpperCase() === 'POLSKA') {
                                            countriesAvailable.push(country);
                                        } else {
                                            const foundedDestination = destiantions.find(
                                                (destination) =>
                                                    NumberUtils.toIntIfPossible(destination?.country?.id) ===
                                                    NumberUtils.toIntIfPossible(country.id)
                                            );
                                            countriesAvailable.push(foundedDestination.country);
                                        }
                                    });
                                }
                                return countryTemplate(data, props, `element.transfers[${props.rowIndex}]`, countriesAvailable, 'transfer');
                            }}
                        />
                    )}
                    {isForeign() && (
                        <Column
                            field='location'
                            header='Miejscowość'
                            body={(element, props) => {
                                return (
                                    <InputTextComponent
                                        id={`location-${props.rowIndex}-transfer`}
                                        name='location'
                                        label='Miejscowość'
                                        colClass='col-12 '
                                        value={element?.location}
                                        showLabel={false}
                                        validator={validator}
                                        validators='required|max:255'
                                        onChange={handleChange}
                                        viewMode={viewMode}
                                        stateField={`element.transfers[${props.rowIndex}]`}
                                    />
                                );
                            }}
                        />
                    )}
                    {isForeign() && (
                        <Column
                            field='type'
                            header='Rodzaj dojazdu'
                            style={{minWidth: '200px'}}
                            colClass='col-12 '
                            body={(element, props) => {
                                return (
                                    <InputDropdownComponent
                                        id={`type-${props.rowIndex}-transfer`}
                                        name='type'
                                        value={element.type}
                                        stateField={`element.transfers[${props.rowIndex}]`}
                                        showLabel={false}
                                        label='Rodzaj dojazdu'
                                        options={[
                                            {enumValue: 'ONE_WAY', label: 'W jedną stronę'},
                                            {enumValue: 'ROUND_TRIP', label: 'W dwie strony'},
                                        ]}
                                        onChange={handleChange}
                                        insideTable
                                        viewMode={viewMode}
                                        validator={validator}
                                        validators={'required'}
                                        colClass='col-12'
                                        optionLabel={'label'}
                                        dataKey='enumValue'
                                    />
                                );
                            }}
                        />
                    )}

                    {isDomestic() && (
                        <Column
                            field='amount'
                            header='Kwota'
                            style={{minWidth: '200px'}}
                            body={(element, props) => {
                                return (
                                    <InputNumberComponent
                                        id={`amount-${props.rowIndex}-transfer`}
                                        name='value'
                                        label={'Kwota'}
                                        value={element?.amount?.value}
                                        onChange={handleChange}
                                        viewMode={viewMode}
                                        showLabel={false}
                                        validator={validator}
                                        validators={StringUtils.isBlank(element.amount) ? 'not_required' : 'required'}
                                        disabled={StringUtils.isBlank(element.amount)}
                                        mode='currency'
                                        colClass='col-12 '
                                        currency='PLN'
                                        locale='pl-PL'
                                        stateField={`element.transfers[${props.rowIndex}].amount`}
                                    />
                                );
                            }}
                        />
                    )}
                    {isDomestic() && (
                        <Column
                            field='typeOfPayment'
                            header='Rodzaj płatności'
                            style={{minWidth: '200px'}}
                            colClass='col-12 '
                            body={(element, props) => {
                                return (
                                    <InputDropdownComponent
                                        id={`typeOfPayment-${props.rowIndex}-transfer`}
                                        name='typeOfPayment'
                                        value={element.typeOfPayment}
                                        stateField={`element.transfers[${props.rowIndex}]`}
                                        showLabel={false}
                                        label='Rodzaj płatności'
                                        options={paymentDictionaries}
                                        optionLabel='name'
                                        onChange={handleChange}
                                        insideTable
                                        disabled={StringUtils.isBlank(element.amount)}
                                        dataKey='id'
                                        viewMode={viewMode}
                                        validator={validator}
                                        validators={StringUtils.isBlank(element.amount) ? 'not_required' : 'required'}
                                        colClass='col-12'
                                        valueView={element.typeOfPayment?.name}
                                    />
                                );
                            }}
                        />
                    )}
                    {isDomestic() && (
                        <Column
                            field='comment'
                            header='Komentarz'
                            body={(element, props) => {
                                return (
                                    <InputTextComponent
                                        id={`comment-${props.rowIndex}-transfer`}
                                        name='comment'
                                        label='Komentarz'
                                        colClass='col-12 '
                                        value={element?.comment}
                                        showLabel={false}
                                        validator={validator}
                                        validators='not_required|max:1024'
                                        onChange={handleChange}
                                        viewMode={viewMode}
                                        stateField={`element.transfers[${props.rowIndex}]`}
                                    />
                                );
                            }}
                        />
                    )}
                    <Column
                        header=''
                        body={(rowData, props) => {
                            const attachmentOpt = {
                                enabled: !StringUtils.isBlank(rowData.amount),
                                handleChange: (e) => handleReceiptChange('transfers', rowData, e),
                                attachment: rowData.receipt,
                            };
                            return actionTemplate(
                                props,
                                rowData,
                                () => {
                                    const transfers = element.transfers.filter((pa) => pa !== rowData);
                                    element.transfers = transfers;
                                    updateElement(element);
                                },
                                attachmentOpt
                            );
                        }}
                    />
                </DataTable>
                {viewMode !== ViewMode.VIEW && (
                    <ActionLink
                        label={isDomestic() ? 'Dodaj przejazd' : 'Dodaj dojazd'}
                        id='add-transfer'
                        iconName='mdi-plus'
                        iconSide='left'
                        iconSize='icon-large'
                        variant='blue'
                        className='float-right'
                        key={'add-transfer-button'}
                        handleClick={() => {
                            const newElement = {
                                country: undefined,
                                location: undefined,
                                comment: undefined,
                                typeOfPayment: undefined,
                                transferType: undefined,
                            };
                            element.transfers.push(newElement);
                            updateElement(element);
                        }}
                    />
                )}
            </div>
        );
    };

    const isForeign = () => {
        return element.type?.enumValue === 'FOREIGN';
    };
    const isDomestic = () => {
        return element.type?.enumValue === 'DOMESTIC';
    };

    return (
        <div className='row'>
            {renderAccommodations()}
            {renderExpenses()}
            {renderVehicleLogs()}
            {isForeign() && renderHospitalStays()}
            {renderlocalTransports()}
            {renderTransfers()}
            {showDialog && renderAttachementDialog()}
        </div>
    );
};

DelegationTravelCostStep.defaultProps = {
    handleChange: undefined,
    viewMode: ViewMode.VIEW,
    element: undefined,
    validator: undefined,
};

DelegationTravelCostStep.propTypes = {
    handleChange: PropTypes.func.isRequired,
    viewMode: PropTypes.string,
    element: PropTypes.object,
    validator: PropTypes.object,
};

export default DelegationTravelCostStep;
